/**
 * RecentEventsSection Component
 * Container component that fetches recent events and displays expandable event cards
 * Design based on Figma specifications
 */

import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Skeleton } from "@nextui-org/react";
import { Calendar } from "lucide-react";

import { ROUTES } from "@/shared/constants/routes";

import { EventItem } from "./EventItem";
import { SectionError } from "./SectionError";
import { useGetRecentEvents } from "../requests/getRecentEvents";

/**
 * Fixed container height from Figma: 640px (same as Tasks)
 * Header: 20px + gap 16px = 36px
 * Padding: 16px top + 16px bottom = 32px
 * Available for scroll: 640 - 36 - 32 = 572px
 */
const CONTAINER_HEIGHT = "h-[640px]";
const SCROLL_AREA_HEIGHT = "max-h-[572px]";

/** Figma shadow specification */
const FIGMA_SHADOW =
  "shadow-[0px_1px_3px_rgba(0,0,0,0.1),0px_1px_2px_rgba(0,0,0,0.06)]";

/**
 * Loading skeleton for recent events section
 */
const RecentEventsSkeleton = () => (
  <div
    className={`flex flex-col items-start p-4 gap-4 bg-white ${FIGMA_SHADOW} rounded-lg ${CONTAINER_HEIGHT}`}
  >
    {/* Header Skeleton */}
    <div className="flex items-center justify-between w-full flex-shrink-0">
      <Skeleton className="h-5 w-44 rounded" />
      <Skeleton className="h-5 w-14 rounded" />
    </div>
    {/* Event Cards Skeleton */}
    <div
      className={`flex flex-col gap-2 w-full overflow-y-auto ${SCROLL_AREA_HEIGHT}`}
    >
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <Skeleton key={i} className="h-[84px] w-full rounded-lg flex-shrink-0" />
      ))}
    </div>
  </div>
);

/**
 * Empty state when no recent events
 */
const RecentEventsEmpty = () => (
  <div
    className={`flex flex-col items-start p-4 gap-4 bg-white ${FIGMA_SHADOW} rounded-lg ${CONTAINER_HEIGHT}`}
  >
    <div className="flex items-center justify-between w-full flex-shrink-0">
      <h3 className="text-sm leading-5">
        <span className="font-medium text-[#0F172B]">Recent Events</span>{' '}
        <span className="font-normal text-[#71717A]">(Last 30d)</span>
      </h3>
    </div>
    <div className="flex flex-col items-center justify-center flex-grow w-full">
      <Calendar className="w-12 h-12 mb-2 text-gray-300" />
      <p className="text-sm font-medium text-gray-500">No recent events</p>
      <p className="text-xs text-gray-400">
        No breach events or alerts in the last 30 days
      </p>
    </div>
  </div>
);

/**
 * Main RecentEventsSection component
 */
export function RecentEventsSection() {
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useGetRecentEvents();

  // Track which event is expanded (only one at a time)
  const [expandedId, setExpandedId] = useState<string | null>(null);

  /**
   * Toggle expand/collapse state for an event
   * Opening a new event automatically closes the previously expanded one
   */
  const toggleExpand = useCallback((id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
  }, []);

  /**
   * Navigate to breach history page
   */
  const handleViewAll = useCallback(() => {
    navigate(`/${ROUTES.THIRD_PARTY_BREACH_HISTORY}`);
  }, [navigate]);

  // Loading state
  if (isLoading) {
    return <RecentEventsSkeleton />;
  }

  // Error state
  if (isError || !data) {
    return (
      <div
        className={`flex flex-col items-start p-4 gap-4 bg-white ${FIGMA_SHADOW} rounded-lg ${CONTAINER_HEIGHT}`}
      >
        <div className="flex items-center justify-between w-full flex-shrink-0">
          <h3 className="text-sm leading-5">
            <span className="font-medium text-[#0F172B]">Recent Events</span>{' '}
            <span className="font-normal text-[#71717A]">(Last 30d)</span>
          </h3>
        </div>
        <div className="flex-grow flex items-center justify-center w-full">
          <SectionError
            title="Failed to load events"
            description="Unable to fetch recent events data"
            onRetry={() => refetch()}
          />
        </div>
      </div>
    );
  }

  // Empty state
  if (!data.events || data.events.length === 0) {
    return <RecentEventsEmpty />;
  }

  // Disable "View All" button if there are 30 or fewer total events
  const isViewAllDisabled = data.total_count <= 30;

  return (
    <div
      className={`flex flex-col items-start p-4 gap-4 bg-white ${FIGMA_SHADOW} rounded-lg ${CONTAINER_HEIGHT}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between w-full flex-shrink-0">
        <h3 className="text-sm leading-5">
          <span className="font-medium text-[#0F172B]">Recent Events</span>{' '}
          {/* <span className="font-normal text-[#71717A]">(Last 30d)</span> */}
        </h3>
        {/* <button
          type="button"
          onClick={handleViewAll}
          disabled={isViewAllDisabled}
          className={`text-sm font-medium leading-5 ${
            isViewAllDisabled
              ? 'text-[#006FEE]/40 cursor-not-allowed'
              : 'text-[#006FEE] hover:opacity-70 transition-opacity'
          }`}
        >
          View All
        </button> */}
      </div>

      {/* Events List with Scroll */}
      <div
        className={`flex flex-col gap-2 w-full overflow-y-auto ${SCROLL_AREA_HEIGHT} pr-1`}
      >
        {data.events.map((event) => (
          <EventItem
            key={event.id}
            event={event}
            isExpanded={expandedId === event.id}
            onToggle={() => toggleExpand(event.id)}
          />
        ))}
      </div>
    </div>
  );
}

export default RecentEventsSection;
